import React from 'react';

const SEGMENT_STATUS = {
    paid: 'paid',       // green
    overdue: 'overdue', // red
    pending: 'pending', // yellow - due this month
    future: 'future'    // gray - later months
};

/**
 * Parses date string to YYYY-MM-DD for comparison (avoids timezone issues).
 * @param {string} dateString - "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ssZ"
 * @returns {string} YYYY-MM-DD
 */
function toDateOnly(dateString) {
    if (!dateString) return '';
    const part = String(dateString).split(/[\sT]/)[0];
    return part || '';
}

/**
 * Compares two YYYY-MM-DD strings as calendar dates in local time (no UTC shift).
 * @returns {boolean} true if dueDate is strictly before today
 */
function isDueDatePast(dueDateStr, todayStr) {
    if (!dueDateStr || !todayStr) return false;
    const [y1, m1, d1] = dueDateStr.split('-').map(Number);
    const [y2, m2, d2] = todayStr.split('-').map(Number);
    if ([y1, m1, d1, y2, m2, d2].some(Number.isNaN)) return false;
    const due = new Date(y1, m1 - 1, d1);
    const today = new Date(y2, m2 - 1, d2);
    return due < today;
}

/**
 * Returns status for a single installment for progress bar segment.
 * API status: 3 = paid, 1 = pending (but we use due_date to determine if overdue).
 * Logic:
 * - Paid (status 3) → green
 * - Due date passed → red (overdue)
 * - Due date in current month → yellow (pending this month)
 * - Due date in future → gray
 * @param {Object} installment - { status: { code }, dates: { due_date, paid_date } }
 * @param {string} today - YYYY-MM-DD
 * @param {string} firstDayThisMonth - YYYY-MM-DD
 * @param {string} lastDayThisMonth - YYYY-MM-DD
 */
function getPaymentPlanSegmentStatus(item, today, firstDayThisMonth, lastDayThisMonth) {
    if (item.status === 'PAID') {
        return SEGMENT_STATUS.paid;
    }

    if (item.status === 'OVERDUE') {
        return SEGMENT_STATUS.overdue;
    }

    const dueDate = toDateOnly(item.due_date);
    if (!dueDate) {
        return SEGMENT_STATUS.future;
    }

    if (isDueDatePast(dueDate, today)) {
        return SEGMENT_STATUS.overdue;
    }

    if (dueDate >= firstDayThisMonth && dueDate <= lastDayThisMonth) {
        return SEGMENT_STATUS.pending;
    }

    return SEGMENT_STATUS.future;
}

function getSegmentStatus(installment, today, firstDayThisMonth, lastDayThisMonth) {
    const statusCode = installment.status?.code != null ? String(installment.status.code) : '';
    const isPaid = statusCode === '3';
    const dueDate = toDateOnly(installment.dates?.due_date);
    if (!dueDate) return SEGMENT_STATUS.future;

    if (isPaid) return SEGMENT_STATUS.paid;

    if (isDueDatePast(dueDate, today)) return SEGMENT_STATUS.overdue;
    if (dueDate >= firstDayThisMonth && dueDate <= lastDayThisMonth) return SEGMENT_STATUS.pending;
    return SEGMENT_STATUS.future;
}

/**
 * Builds first and last day of current month as YYYY-MM-DD.
 */
function getMonthBounds() {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const first = `${y}-${m}-01`;
    const lastDay = new Date(y, now.getMonth() + 1, 0);
    const d = String(lastDay.getDate()).padStart(2, '0');
    const last = `${y}-${m}-${d}`;
    const today = `${y}-${m}-${String(now.getDate()).padStart(2, '0')}`;
    return { today, firstDayThisMonth: first, lastDayThisMonth: last };
}

const SEGMENT_CLASS = {
    [SEGMENT_STATUS.paid]: 'installment-segment--paid',
    [SEGMENT_STATUS.overdue]: 'installment-segment--overdue',
    [SEGMENT_STATUS.pending]: 'installment-segment--pending',
    [SEGMENT_STATUS.future]: 'installment-segment--future'
};

const SEGMENT_TITLE = {
    [SEGMENT_STATUS.paid]: 'Pagada',
    [SEGMENT_STATUS.overdue]: 'Vencida',
    [SEGMENT_STATUS.pending]: 'Vence este mes',
    [SEGMENT_STATUS.future]: 'Cuota futura'
};

function InstallmentProgressBar({
    installments = [],
    paymentPlanItems = null,
    totalCount,
    className = '',
}) {
    const isPaymentPlan = Boolean(paymentPlanItems?.length);
    const sourceItems = isPaymentPlan ? paymentPlanItems : installments;

    const fromItems = sourceItems.length
        ? Math.max(sourceItems.length, ...sourceItems.map((item) => (item.number != null ? item.number : 0)))
        : 0;
    const count = (totalCount != null && totalCount > 0) ? totalCount : fromItems;
    if (count === 0) return null;

    const { today, firstDayThisMonth, lastDayThisMonth } = getMonthBounds();

    const byNumber = {};
    sourceItems.forEach((item) => {
        const num = item.number != null ? item.number : (Object.keys(byNumber).length + 1);
        byNumber[num] = item;
    });

    const segments = [];
    for (let i = 1; i <= count; i++) {
        const item = byNumber[i] || {};
        const status = isPaymentPlan
            ? getPaymentPlanSegmentStatus(item, today, firstDayThisMonth, lastDayThisMonth)
            : getSegmentStatus(item, today, firstDayThisMonth, lastDayThisMonth);
        segments.push(status);
    }

    const paidCount = segments.filter((status) => status === SEGMENT_STATUS.paid).length;
    const progressLabel = isPaymentPlan
        ? `Progreso del plan: ${paidCount} de ${count} compromisos cumplidos`
        : `Progreso de cuotas: ${paidCount} de ${count} pagadas`;

    return (
        <div
            className={`installment-progress-bar ${className}`}
            role="progressbar"
            aria-valuenow={paidCount}
            aria-valuemin={0}
            aria-valuemax={count}
            aria-label={progressLabel}
        >
            <div className="installment-progress-bar__track">
                {segments.map((status, index) => (
                    <div
                        key={index}
                        className={`installment-segment ${SEGMENT_CLASS[status]}`}
                        title={`Cuota ${index + 1}: ${SEGMENT_TITLE[status]}`}
                    />
                ))}
            </div>
        </div>
    );
}

export default InstallmentProgressBar;
